/**
 * Build year-over-year AADT scaling ratios for VMT backcasting (2015–2022).
 *
 * `aadt_filtered.value` is documented as summed Caltrans AADT (traffic index), but production
 * Supabase rows store precomputed YoY ratios (≈0.7–1.4). This module supports both shapes.
 */

export type AadtYearValue = { year: number; value: number };

const RATIO_MAX = 20;
const RAW_COUNT_MIN = 10_000;

/**
 * True when values look like dimensionless YoY ratios (not place-level AADT sums).
 */
export function isPrecomputedAadtYoYRatios(values: number[]): boolean {
  if (values.length === 0) return false;
  if (values.some((v) => !Number.isFinite(v) || v <= 0)) return false;
  if (values.some((v) => v >= RAW_COUNT_MIN)) return false;
  return values.every((v) => v > 0 && v <= RATIO_MAX);
}

function dedupeByYear(rows: AadtYearValue[]): Map<number, number> {
  const map = new Map<number, number>();
  for (const row of rows) {
    const y = Number(row.year);
    const v = Number(row.value);
    if (!Number.isFinite(y) || !Number.isFinite(v) || map.has(y)) continue;
    map.set(y, v);
  }
  return map;
}

/**
 * Returns map: backcast year Y → ratio such that VMT_Y = VMT_{Y+1} × ratio.
 * Years are filtered to [minYear, maxYear] (inclusive).
 */
export function buildAadtBackcastRatiosByYear(
  rows: AadtYearValue[],
  opts?: { minYear?: number; maxYear?: number }
): Map<number, number> {
  const minYear = opts?.minYear ?? 2015;
  const maxYear = opts?.maxYear ?? 2022;
  const byYear = dedupeByYear(rows);
  const values = Array.from(byYear.values());
  const out = new Map<number, number>();

  if (isPrecomputedAadtYoYRatios(values)) {
    for (const [year, ratio] of byYear) {
      if (year >= minYear && year <= maxYear && ratio > 0) {
        out.set(year, ratio);
      }
    }
    return out;
  }

  const sortedYears = Array.from(byYear.keys()).sort((a, b) => a - b);
  for (let i = 0; i < sortedYears.length - 1; i++) {
    const currentYear = sortedYears[i];
    const nextYear = sortedYears[i + 1];
    if (currentYear < minYear || currentYear > maxYear) continue;
    const currentValue = byYear.get(currentYear)!;
    const nextValue = byYear.get(nextYear)!;
    if (currentValue > 0 && nextValue > 0) {
      out.set(currentYear, currentValue / nextValue);
    }
  }
  return out;
}

/** Sorted ratio list for DataService-style consumers ({ year, ratio } descending). */
export function buildAadtBackcastRatioList(
  rows: AadtYearValue[],
  opts?: { minYear?: number; maxYear?: number }
): { year: number; ratio: number }[] {
  const map = buildAadtBackcastRatiosByYear(rows, opts);
  return Array.from(map.entries())
    .map(([year, ratio]) => ({ year, ratio }))
    .sort((a, b) => b.year - a.year);
}
